import React, { useEffect, useRef, useState } from 'react';
import { Image, StyleSheet, Text, View } from 'react-native';

import { useRemScale } from 'lib/remScale';
import { TYPE, FONTS } from 'style/typography';
import { currentWordIndex, sentenceAt } from '../captions';

const AFTER_NARRATION_MS = 900;

/**
 * The guided first visit's short slide: the course's own lesson picture, read aloud by the course's tutor, with the
 * spoken word lit in the caption. It finishes by itself when the narration ends (owner: no extra buttons). If the
 * audio cannot play (blocked or failed), the caption still runs on the word timings and the slide still finishes.
 */
export default function TourSlidePanel({ entry, onDone }) {
  const { rem } = useRemScale();
  const [time, setTime] = useState(-1);
  const finished = useRef(false);

  useEffect(() => {
    if (!entry?.slide) return undefined;
    finished.current = false;
    const words = entry.slide.words || [];
    const length = entry.slide.duration || (words.length ? words[words.length - 1].e : 6);
    let audio = null;
    let frame = 0;
    let clockStart = null;
    let doneTimer = null;

    const finish = () => {
      if (finished.current) return;
      finished.current = true;
      doneTimer = setTimeout(() => onDone && onDone(), AFTER_NARRATION_MS);
    };
    // Without audio the caption keeps its own clock.
    const tick = now => {
      if (audio && !audio.paused && !Number.isNaN(audio.currentTime)) {
        setTime(audio.currentTime);
      } else if (clockStart !== null) {
        const elapsed = (now - clockStart) / 1000;
        setTime(elapsed);
        if (elapsed >= length) finish();
      }
      if (!finished.current) frame = requestAnimationFrame(tick);
    };
    const runWithoutAudio = () => {
      if (clockStart === null) clockStart = performance.now();
    };

    try {
      audio = new window.Audio(entry.slide.audio);
      audio.addEventListener('ended', finish);
      audio.addEventListener('error', runWithoutAudio);
      const playing = audio.play();
      if (playing && typeof playing.catch === 'function') playing.catch(runWithoutAudio);
    } catch (_) {
      runWithoutAudio();
    }
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(doneTimer);
      if (audio) {
        audio.pause();
        audio.removeEventListener('ended', finish);
        audio.removeEventListener('error', runWithoutAudio);
      }
    };
  }, [entry, onDone]);

  if (!entry?.slide) return null;
  const words = entry.slide.words || [];
  const index = currentWordIndex(words, time);
  const sentence = sentenceAt(words, index);

  return (
    <View style={styles.overlay} testID="first-visit-slide">
      <View style={[styles.card, { borderRadius: rem(20), padding: rem(16), width: rem(760), maxWidth: '92%' }]}>
        <Text style={[styles.tutor, { fontSize: rem(TYPE.sm), fontFamily: FONTS.body, marginBottom: rem(10) }]}>
          {`${entry.tutor} · ${entry.unitTitle || ''}`}
        </Text>
        <Image
          source={{ uri: entry.slide.image }}
          style={[styles.picture, { borderRadius: rem(14), height: rem(380) }]}
          resizeMode="cover"
          accessibilityLabel={entry.slide.text}
        />
        <Text style={[styles.caption, { fontSize: rem(TYPE['2xl']), fontFamily: FONTS.heading, marginTop: rem(14), minHeight: rem(64) }]}>
          {sentence.map(word => (
            <Text key={word.index} style={word.index === index ? styles.spoken : null}>
              {`${word.w} `}
            </Text>
          ))}
        </Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  overlay: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0,0,0,0.45)',
    zIndex: 92,
  },
  card: {
    backgroundColor: 'rgba(15,16,20,0.96)',
    borderWidth: 1,
    borderColor: 'rgba(197,232,54,0.30)',
  },
  tutor: { color: '#C5E836', fontWeight: '700', letterSpacing: 0.4 },
  picture: { width: '100%', backgroundColor: 'rgba(255,255,255,0.05)' },
  caption: { color: 'rgba(246,251,255,0.82)', fontWeight: '600', textAlign: 'center' },
  spoken: { color: '#C5E836' },
});
